/**
 * Tiny pub/sub for "auth has expired and we couldn't refresh." Used to
 * decouple the tRPC fetch wrapper and the proactive-refresh scheduler from
 * AuthContext (which subscribes to clear in-memory auth + redirect to
 * sign-in).
 *
 * The fetch wrapper can't import AuthContext directly (AuthContext consumes
 * tRPC for the refresh call → circular dep). Pub/sub keeps both sides
 * dependency-free.
 */

type Listener = () => void | Promise<void>;

let listeners: Listener[] = [];

/** Notify every listener that the user's session is gone. Awaits any async
 *  cleanup (e.g. AsyncStorage clear) so callers can sequence after teardown. */
export async function emitAuthExpired(): Promise<void> {
  await Promise.all(listeners.map((l) => Promise.resolve().then(l)));
}

/** Subscribe a listener. Returns an unsubscribe function. */
export function subscribeAuthExpired(listener: Listener): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

/** For tests: drop all subscribers. */
export function _resetAuthEvents(): void {
  listeners = [];
}
